/**
 * M0a grey-box test room — a measuring instrument, not a scene.
 *
 * Sanctioned placeholder per docs/decisions.md D-004: it is deliberately drab so
 * that motion problems cannot hide behind art. It must be gone by the end of M0b.
 *
 * Geometry is chosen to make game feel legible: flat ground to read ice-skating,
 * a step and a ramp to read landings and slope handling, a low wall to read
 * camera collision, and a long open run to read roll momentum.
 */

import * as THREE from 'three';

/**
 * Rosalie's intended footprint, in metres. A piglet-sized plush sow.
 *
 * D-012 locks this. **Length and height have never moved and must not** — every
 * argument that decision makes rests on them: the step ladder below, why she can
 * escape where Otto cannot, why the farm reads as hostile architecture.
 *
 * Width came down from 0.34 to 0.30 on 2026-08-07 at G1, because at 0.34 she
 * read as fat head-on: you see her widest cross-section flanking her face, and
 * 0.34 across a 0.55 m body is proportionally far wider than any real pig. See
 * the D-012 amendment.
 */
export const ROSALIE_SCALE = { length: 0.55, width: 0.3, height: 0.32 } as const;

// Drab, but NOT low-contrast. R-004 came back "I can barely see the objects as
// they have nearly the same colors" — the first three greys spanned 110→189 in
// sRGB, which collapses further once lit and shadowed. A measuring instrument you
// cannot read is not a measuring instrument.
//
// So: one value per function, widely spaced, plus a slight hue offset so the two
// dark objects stay distinguishable from each other. This is legibility, not
// dressing — D-004's "deliberately drab" rule is about not hiding motion problems
// behind ART, and unreadable geometry hides them just as effectively.
const GROUND = 0x8f8f8f; // mid — the reference value everything else reads against
const STEPS = 0xdedede; // lightest — landing weight, step-up handling
const RAMP = 0x50555b; // dark, faintly cool — slope traversal, roll acceleration
const WALL = 0x33373c; // darkest — camera collision sweep, cover

/**
 * One solid in the room: half-extents, centre, and an optional pitch about X.
 *
 * The meshes AND the physics colliders are both generated from this list, which
 * is the point of it existing. A test room whose collision does not match what
 * you can see is not a measuring instrument, it is a source of phantom bugs —
 * and "she clips the ramp" would be indistinguishable from a controller defect.
 */
export interface TestRoomSolid {
  name: string;
  half: { x: number; y: number; z: number };
  pos: { x: number; y: number; z: number };
  /** Rotation about X in radians. Kept for boxes; the ramp no longer uses it. */
  pitch?: number;
  colour: number;
}

/**
 * The ramp, as a real wedge rather than a tilted slab.
 *
 * A tilted box was the obvious way to build this and it did not work, for a
 * reason worth recording. However you place it, the slab either leaves a
 * vertical lip at its foot that autostep will not climb — there is no flat
 * landing at the top of a lip that is immediately a slope — or, if you sink it
 * far enough that the lip disappears, it INTERSECTS the ground slab. At the
 * shallow seam where the two overlap, Rapier's character controller receives
 * contradictory contacts and slides the character sideways along the ramp
 * instead of up it. Both failure modes look identical from the outside: the
 * slope is simply unreachable, and Alex hit exactly that.
 *
 * A wedge fixes both by construction. Its underside lies flat ON the floor at
 * y = 0 so nothing intersects, and its sloped face begins at zero height so
 * there is nothing to step onto.
 */
export interface TestRoomWedge {
  name: string;
  /** Centre in X, and half-width. */
  x: number;
  halfWidth: number;
  /** Ground-level end. */
  lowZ: number;
  /** Raised end. */
  highZ: number;
  /** Height at `highZ`. Zero at `lowZ`, by definition. */
  height: number;
  colour: number;
}

/** The one wedge in the room. Exported separately because its shape differs. */
export function testRoomWedges(): TestRoomWedge[] {
  return [
    {
      name: 'ramp',
      x: 7,
      halfWidth: 2,
      lowZ: -10,
      highZ: 0,
      // ~10 degrees over a 10 m run. Long enough that roll acceleration has
      // somewhere to build, which is spec 5.4's highest-risk unknown.
      height: 1.8,
      colour: RAMP,
    },
  ];
}

/**
 * The wedge's six corners, in world space and in a fixed order.
 *
 * Shared by the mesh builder and the collider builder so the two cannot drift.
 * Order: 0,1 low edge (left,right); 2,3 high edge at floor level; 4,5 high edge
 * at full height.
 */
export function wedgeCorners(w: TestRoomWedge): [number, number, number][] {
  const xl = w.x - w.halfWidth;
  const xr = w.x + w.halfWidth;
  return [
    [xl, 0, w.lowZ],
    [xr, 0, w.lowZ],
    [xl, 0, w.highZ],
    [xr, 0, w.highZ],
    [xl, w.height, w.highZ],
    [xr, w.height, w.highZ],
  ];
}

/** Every solid in the room, mesh and collider alike. */
export function testRoomSolids(): TestRoomSolid[] {
  const solids: TestRoomSolid[] = [];

  // Ground. A thin slab rather than an infinite plane so the collider is the
  // same kind of object as everything else here.
  solids.push({
    name: 'ground',
    half: { x: 20, y: 0.05, z: 20 },
    pos: { x: 0, y: -0.05, z: 0 },
    colour: GROUND,
  });

  // Steps — read landing weight and step-up handling. Heights are expressed as
  // multiples of her own height on purpose: "can she step onto this" is a question
  // about her body, not about metres, and D-012 locked the body. If ROSALIE_SCALE
  // ever moves, the ladder moves with it and stays meaningful.
  //
  //   0.25x trivial — she should walk over it without a thought
  //   0.5x  normal  — the everyday step-up, where landing weight reads best
  //   1.0x  hard    — a scramble; should look like effort
  //   1.5x  refused — she cannot make it. The failure must read as "too big for
  //                   me", not as a stuck controller. That is the whole "built for
  //                   boots, not for her" thesis, in one test object.
  const H = ROSALIE_SCALE.height;
  for (const [i, mult] of [0.25, 0.5, 1.0, 1.5].entries()) {
    const h = H * mult;
    solids.push({
      name: `step-${mult}x`,
      half: { x: 1.5, y: h / 2, z: 1.2 },
      pos: { x: -6, y: h / 2, z: -3 - i * 2.6 },
      colour: STEPS,
    });
  }

  // The farm's own step, in the room where nothing else is in the frame.
  //
  // D-146: she cannot walk onto a 0.09 m pen deck in the fattening hall, and has
  // not been able to since session 17. Every rung above is a multiple of HER, and
  // that is right for "is this built for me" — but the deck is a number the farm
  // chose, it is BELOW the 0.08 m rung she walks over without a thought, and the
  // hall's version of it refuses her. The two facts cannot both be about height,
  // so the height needs its own object here to be ruled in or out.
  //
  // Restated rather than imported from `fattening.ts`, and the reason is
  // mechanical: `rosalie.ts` reads ROSALIE_SCALE out of THIS file, so an import
  // the other way closes a module cycle and the game boots into
  // "Cannot access 'ROSALIE_SCALE' before initialization". The hall and the
  // farrowing house already keep their own copies of this number
  // (`DECK_Y`, `SLAT_HEIGHT`); `deck-step.spec` in movement.spec.ts asserts all
  // three still agree, which is the guard the import would have been.
  const DECK_HEIGHT = 0.09;
  solids.push({
    name: 'deck-step',
    half: { x: 1.5, y: DECK_HEIGHT / 2, z: 1.2 },
    pos: { x: -6, y: DECK_HEIGHT / 2, z: 4 },
    colour: STEPS,
  });

  // Crawl tunnel — the one thing crouch-low is FOR, and the room had nothing of
  // the kind. Spec §4 gives her a crouch and §5.1 lists "under slats" as cover,
  // but with no low gap anywhere there was no way to judge either: you could
  // press Ctrl and watch her get shorter, and that was the whole test.
  //
  // Clearance is under her 0.32 m standing height, so walking in is refused,
  // and over her crouched collider so creeping through works. Rolling
  // through is deliberately NOT viable: she rides up to 0.275 m on her long
  // axis mid-tumble, so the tunnel wants the quiet verb, not the fast one.
  // 0.28 m: over the 0.26 m crouched collider with margin, under the 0.32 m
  // standing one. Both figures are capsule heights, and a capsule cannot be
  // shorter than twice its radius, which is what puts the floor under 0.26.
  const TUNNEL = { x: -12, z: -2, halfZ: 2, clearance: 0.28 };
  solids.push({
    name: 'tunnel-roof',
    half: { x: 1.2, y: 0.05, z: TUNNEL.halfZ },
    pos: { x: TUNNEL.x, y: TUNNEL.clearance + 0.05, z: TUNNEL.z },
    colour: STEPS,
  });
  for (const side of [-1, 1]) {
    solids.push({
      name: `tunnel-wall-${side > 0 ? 'r' : 'l'}`,
      half: { x: 0.1, y: TUNNEL.clearance / 2, z: TUNNEL.halfZ },
      pos: { x: TUNNEL.x + side * 1.1, y: TUNNEL.clearance / 2, z: TUNNEL.z },
      colour: WALL,
    });
  }

  // A single low beam, away from the tunnel. Same clearance, no side walls and
  // no length: it isolates "can she get under a thing" from "can she navigate a
  // confined space", which are different questions and fail differently.
  solids.push({
    name: 'crawl-beam',
    half: { x: 1.5, y: 0.05, z: 0.15 },
    pos: { x: -12, y: TUNNEL.clearance + 0.05, z: 4 },
    colour: STEPS,
  });

  // The ramp is a WEDGE, not a box — see testRoomWedges().

  // Low wall — read camera collision sweep, and cover for the M0b stealth skeleton.
  solids.push({
    name: 'wall',
    half: { x: 0.15, y: 0.6, z: 4 },
    pos: { x: 0, y: 0.6, z: 6 },
    colour: WALL,
  });

  return solids;
}

export function createTestRoom(): THREE.Group {
  const room = new THREE.Group();
  room.name = 'testroom';

  for (const solid of testRoomSolids()) {
    const mesh = new THREE.Mesh(
      new THREE.BoxGeometry(solid.half.x * 2, solid.half.y * 2, solid.half.z * 2),
      new THREE.MeshStandardMaterial({ color: solid.colour, roughness: 0.9, metalness: 0 }),
    );
    mesh.name = solid.name;
    mesh.position.set(solid.pos.x, solid.pos.y, solid.pos.z);
    if (solid.pitch !== undefined) mesh.rotation.x = solid.pitch;
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    room.add(mesh);
  }

  for (const w of testRoomWedges()) {
    const c = wedgeCorners(w);
    const geo = new THREE.BufferGeometry();
    const v: number[] = [];
    // Faces, wound counter-clockwise when seen from outside.
    const tri = (a: number, b: number, d: number): void => {
      const pa = c[a];
      const pb = c[b];
      const pd = c[d];
      if (!pa || !pb || !pd) return;
      v.push(...pa, ...pb, ...pd);
    };
    // NO underside. It lies exactly on the floor plane, so drawing it put two
    // coplanar surfaces at identical depth and the ramp flickered along its
    // whole length. It is also unobservable — the wedge sits on the ground —
    // so the fix costs nothing. The collider is a convex hull of the corners
    // and does not care which faces are drawn.
    // Winding matters and was wrong in the first version: every face pointed
    // INWARD, so back-face culling made the ramp see-through and you looked
    // straight past it to the ground and grid underneath. That is what the
    // "weird flickering graphics artifacts" actually were — not z-fighting at
    // all, though the coplanar underside was a real second problem.
    //
    // Each triple below is counter-clockwise seen from OUTSIDE the solid.
    tri(0, 4, 1); tri(1, 4, 5);   // the slope, normals up
    tri(2, 3, 4); tri(3, 5, 4);   // vertical back face, normals +Z
    tri(0, 2, 4);                 // left side, normals -X
    tri(1, 5, 3);                 // right side, normals +X
    geo.setAttribute('position', new THREE.Float32BufferAttribute(v, 3));
    geo.computeVertexNormals();
    const mesh = new THREE.Mesh(
      geo,
      new THREE.MeshStandardMaterial({
        color: w.colour,
        roughness: 0.9,
        metalness: 0,
        // Near the low edge the slope is still within microns of the floor, so
        // removing the underside is not quite enough on its own. This biases
        // the ramp toward the viewer in the depth buffer — the standard remedy
        // for coplanar surfaces, and it moves no geometry, so the mesh and the
        // collider stay derived from exactly the same six corners.
        polygonOffset: true,
        polygonOffsetFactor: -1,
        polygonOffsetUnits: -2,
      }),
    );
    mesh.name = w.name;
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    room.add(mesh);
  }

  // 1 m grid so speed and stride length are readable by eye.
  const grid = new THREE.GridHelper(40, 40, 0x555555, 0x777777);
  grid.position.y = 0.002;
  room.add(grid);

  return room;
}

/**
 * Neutral three-point-ish lighting. Deliberately boring — all art-directed
 * lighting is M0b work (docs/decisions.md D-001).
 */
export function createTestLighting(): THREE.Group {
  const rig = new THREE.Group();
  rig.name = 'testroom-lighting';

  // Ambient fill is kept low ON PURPOSE. At 1.4 it washed cast shadows out
  // almost entirely — they were being rendered correctly and were simply
  // invisible. Contact shadows are how landing weight reads, so the grey-box
  // room must show them clearly even though it is otherwise drab.
  rig.add(new THREE.HemisphereLight(0xdfe6ee, 0x2a2a2a, 0.55));

  // Lower sun elevation than the obvious choice, so shadows are long enough to
  // read the shape of what cast them.
  const key = new THREE.DirectionalLight(0xffffff, 2.4);
  key.position.set(6, 5.5, 4);
  key.castShadow = true;
  key.shadow.mapSize.set(2048, 2048);
  key.shadow.camera.near = 0.5;
  key.shadow.camera.far = 40;
  key.shadow.camera.left = -20;
  key.shadow.camera.right = 20;
  key.shadow.camera.top = 20;
  key.shadow.camera.bottom = -20;
  // Three does NOT refresh the shadow camera's projection when these bounds
  // change, so without this the frustum silently stays at its ±5 default and
  // anything further out casts no shadow at all.
  key.shadow.camera.updateProjectionMatrix();
  // The target must be in the scene graph for its world matrix to be updated.
  rig.add(key);
  rig.add(key.target);

  return rig;
}
